<!--赖传峰-->
<!--2022/4/6-->
<template>
  <ClientOnly>
    <template v-if="!layoutStore.isMobile">
      <ElDialog
        v-model="visible"
        :class="reviewPanelType"
        :close-on-click-modal="closeOnClickModal"
        :close-on-press-escape="closeOnClickModal"
        :show-close="false"
        append-to-body
        class="login-dialog login-wrapper "
        modal-class="login-overlay"
        width="400px"
      >
        <LoginHeaderText v-if="showHeaderText" />
        <div
          id="login-dialog"
          :class="reviewPanelType"
        />
      </ElDialog>
    </template>
    <template v-else>
      <ElDrawer
        v-model="visible"
        :close-on-click-modal="closeOnClickModal"
        append-to-body
        class="login-drawer"
        direction="btt"
        modal-class="login-drawer login-wrapper"
        size="460px"
      >
        <div
          id="login-dialog"
          :class="reviewPanelType"
        />
      </ElDrawer>
    </template>
  </ClientOnly>
</template>

<script
  lang="ts"
  setup
>
import {
  authByLocalStorage,
  onMounted,
  onUnmounted,
  ref,
  useCookie,
  watch,
} from '#imports'
import { useLogin } from '@/composables/useLogin'
import { useLayout } from '@/store/layout'
import { useUserInfo } from '@/store/userInfo'
import $track from '@/track/webYunpanLogin'
import $publicTrack from '@/track/webYunpanPublicModule'
import {
  useEventListener,
  useMutationObserver,
} from '@vueuse/core'
import { useRoute } from 'vue-router'
import { getTaskStatus } from '~/api'
import useActionsInQS from '~/composables/useActionsInQS'
import useReviewPanel from '~/composables/useReviewPanel'
import {
  CustomEventType,
  RoutePath,
  RoutePathOfDrive,
  RouteQueryKeyActionValue,
  StorageKey,
  VipTaskStatus,
} from '~/types/enum'
import LoginHeaderText from './LoginHeaderText.vue'

const userInfoStore = useUserInfo()
const layoutStore = useLayout()
const route = useRoute()
const userInfo = useUserInfo()
useReviewPanel()

let afterLogin: undefined | VoidFunction
let afterClose: undefined | VoidFunction

const visible = ref(false)
const loadLogin = ref(false)
const reviewPanelType = ref('')
const closeOnClickModal = ref(true)
const observerRef = ref<ReturnType<typeof useMutationObserver>>()
const showHeaderText = ref(true)

const redirect2DrivePath = (path: string) => Object.values<string>(RoutePath).includes(path)

watch(
  () => layoutStore.isMobile,
  () => {
    loadLogin.value = false
    observerRef.value?.stop()
  },
)

watch(visible, async (val) => {
  if (!val || loadLogin.value) return
  useLogin('#login-dialog', async () => {
    await userInfoStore.refresh()
    visible.value = false
    closeOnClickModal.value = true
    if (afterLogin) {
      afterLogin()
    } else if (redirect2DrivePath(route.path)) {
      // 不支持第三方登录
      window.location.href = RoutePathOfDrive.ALL
    }
  }).then(() => {
    loadLogin.value = true
    setTimeout(() => {
      const shieldDom = document.querySelector('#shield-app')
      if (shieldDom) {
        observerRef.value = useMutationObserver(
          shieldDom as HTMLDivElement,
          () => {
            if (!shieldDom.childElementCount) {
              reviewPanelType.value = ''
              closeOnClickModal.value = true
            } else {
              closeOnClickModal.value = false
            }
          },
          { childList: true },
        )
      }
    }, 1000)
  })
})

let loginScene = 'main_page_ext'
watch(visible, (val) => {
  if (val) {
    $track.web_login_page_show()
  } else {
    $track.web_login_page_close(loginScene)
    afterClose?.()
  }
})

const actionsInQS = useActionsInQS()
useEventListener(CustomEventType.AFTER_LOGIN, () => {
  if (
    !visible.value
    || !actionsInQS.value.includes(RouteQueryKeyActionValue.LOGIN_TO_DRIVE)
    || afterLogin
    || !redirect2DrivePath(route.path)
  ) {
    return
  }

  window.location.href = RoutePathOfDrive.ALL
})

useEventListener(CustomEventType.AFTER_LOGIN, async () => {
  // 通过是否获取过免费vip判断是否新用户注册
  const vipStatus = (await getTaskStatus('free_vip'))?.data?.value?.result
  userInfo.getFreeVipCalled = true
  useCookie(StorageKey.IS_NEW_USER, { maxAge: 7 * 24 * 60 * 60 }).value = String(vipStatus === VipTaskStatus.NEW_USER)
})

onMounted(() => {
  // 不能直接在 visible ref 中赋值，因为这是客户端逻辑，需要判断 localStorage
  visible.value = actionsInQS.value.includes(RouteQueryKeyActionValue.SHOW_LOGIN)
    && !authByLocalStorage()
  if (visible.value) {
    loginScene = 'main_page_first'
  }
})

const handleReviewPanel = (e: MessageEvent) => {
  if (!e.data) return
  const type = e.data.type as string
  const eventData = e.data.data
  switch (type) {
    case 'review-panel-type':
      reviewPanelType.value = eventData.type
      break
    case 'review-track-event':
      if (eventData.type) {
        $publicTrack[eventData.type as keyof typeof $publicTrack]?.(
          eventData.extdata,
        )
      }
      break
    default:
      break
  }
}

const logout = () => {
  if (route.query.action === RouteQueryKeyActionValue.LOGIN_REQUIRED) {
    visible.value = true
    closeOnClickModal.value = false
  }
}

const init = async () => {
  try {
    const isLogin = await window.loginUI?.isLoginStateValid()
    if (isLogin) return
    if (route.query.action === RouteQueryKeyActionValue.LOGIN_REQUIRED) {
      visible.value = true
      closeOnClickModal.value = false
    }
  } catch (err) {
    console.log('loginDialog init error: ', err)
  }
}

onMounted(() => {
  init()
  window.addEventListener('message', handleReviewPanel)
  window.addEventListener(CustomEventType.LOGOUT_SUCCESS, logout)
})

onUnmounted(() => {
  window.removeEventListener('message', handleReviewPanel)
  window.removeEventListener(CustomEventType.LOGOUT_SUCCESS, logout)
})

defineExpose({
  show(args: {
    afterLogin?: VoidFunction;
    afterClose?: VoidFunction;
    scene?: string;
    showHeaderText?: boolean
  } = { showHeaderText: true }) {
    afterLogin = args.afterLogin
    afterClose = args.afterClose
    loginScene = args.scene || 'main_page_ext'
    visible.value = true
    showHeaderText.value = !!args?.showHeaderText
  },
})
</script>

<style lang="less">
@import "@/assets/styles/util.less";

.login-overlay {
  .el-overlay-dialog {
    display: flex;
    align-items: center;
  }

  .el-dialog {
    transform: unset;
    top: unset;
    position: static;
  }
}

.login-dialog {
  .el-dialog {
    &__header {
      z-index: 1;
      position: relative;
      padding: 0;
    }

    &__headerbtn {
      top: 16px;
      right: 16px;
    }

    &__body {
      padding: 0 0 10px;
    }
  }
}

.login-wrapper {
  #shield-app {
    width: 100% !important;
    height: 100% !important;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 16px;

    &:empty {
      visibility: hidden;
    }
  }
}

div#shield-app {
  visibility: visible;

  > div:first-child {
    > div:first-child {
      position: absolute !important;
      inset: 0 !important;
      width: auto !important;
      height: auto !important;
      background-color: transparent !important;

      > span {
        display: none;
      }
    }
  }
}

.verifyCode div#shield-app {
  visibility: visible;

  > div:first-child {
    > div:first-child {
      width: 430px !important;
      height: 290px !important;
      padding: 12px 16px;
      border: 1px solid #f2f3f4;
      border-radius: 12px;

      > span {
        top: 32px !important;
        right: 28px !important;
      }
    }
  }
}

.pikpak-login-wrapper {
  padding: 0 24px !important;
}

@media screen and (max-width: @mobile-break-width) {
  // 针对登录框的适配
  .pikpak-login-wrapper {
    width: 100% !important;
    padding: 0 16px !important;
  }

  .login-wrapper {
    --el-border-radius-small: 0 !important;
    --el-dialog-width: 100% !important;

    .el-dialog {
      &__header {
        padding: 32px 0 0;
      }

      &__headerbtn {
        top: 0;
        right: 0;
      }

      &__body {
        padding: 0 0 10px;
      }
    }

    div#shield-app {
      width: 100vw;
      height: 100vh;
    }

    .pp-login-panel {
      .pp-login-container {
        max-width: unset;
      }
    }
  }
}

.login-drawer {
  .el-drawer {
    border-radius: 16px 16px 0px 0px;

    &__header {
      margin-bottom: 6px;
    }

    &__body {
      padding: 0;
    }
  }
}
</style>
